<template>
  <footer
    :class="[
      'footer',
      short ? 'footer--short' : '',
    ]"
  >
    <section class="footer__section footer__section--info" v-if="!short">
      <div class="footer__sphere" ref="sphere">
        <AppHeadline
          :data="{
            content: 'Qualify',
            type: 2,
          }"
        />
      </div>
      <AppParagraph
        ref="text"
        :data=" {
          content: 'Finish the intro<br>and<br>visit our website'
        }"
      />

      <span class="section__line" ref="line"></span>
        <!-- @click.native="$eventHub.$emit('showModal')" -->
      <AppButton
        ref="button"

        :data=" {
          content: 'Visit Website'
        }"
      />
    </section>
    <ul class="footer__section footer__section--links" ref="voices">      
    </ul>
  </footer>
</template>

<script>
import { TweenMax, TimelineMax, Power4 } from 'gsap/TweenMax';
import AppHeadline from '@/components/AppHeadline.vue';
import AppParagraph from '@/components/AppParagraph.vue';
import AppButton from '@/components/AppButton.vue';

import raf from '@/scripts/raf';

export default {
  name: 'AppFooter',
  components: {
    AppHeadline,
    AppParagraph,
    AppButton,
  },
  props: {
    short: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isAnimated: false,
      id: null,
      links: [
        { content: 'About', url: '/about', routerLink: true },
        { content: 'How we work', url: '/how-we-work', routerLink: true },
        { content: 'Affiliates', url: '/affiliates', routerLink: true },
        { content: 'Insights', url: '/insights', routerLink: true },
        { content: 'Contacts', url: '/contact', routerLink: true },
        { content: 'Privacy Policy', url: '/privacy', routerLink: true },
        { content: 'Terms of Use', url: '/terms-of-use', routerLink: true },
      ],
    };
  },
  mounted() {
    this.id = `footer-${this.$route.name}`;
    this.init();
    raf.add(() => this.enter(), this.id);
  },
  methods: {
    init() {
      if (!this.short) {
        TweenMax.set(this.$refs.sphere, { autoAlpha: 0, scale: 1.5 });
        TweenMax.set(this.$refs.text.$el, { autoAlpha: 0, y: 50 });
        TweenMax.set(this.$refs.line, { scaleY: 0, transformOrigin: '50% 0' });
        TweenMax.set(this.$refs.button.$el, { autoAlpha: 0, y: 50 });
      }
      TweenMax.set(this.$refs.voices, { autoAlpha: 0, y: 50 });
    },
    enter() {
      const dinstance = this.short ? window.innerHeight - 50 : window.innerHeight * 0.5;
      if (this.$el.getBoundingClientRect().top < dinstance) {
        this.animated = true;
        raf.remove(this.id);
        this.enterAnimation();
      }
    },
    enterAnimation() {
      const tl = new TimelineMax();
      tl.addLabel('start');

      if (!this.short) {
        tl.to(this.$refs.sphere, 1, {
          autoAlpha: 1,
          scale: 1,
          ease: Power4.easeOut,
          clearProps: 'all',
        }, 'start')
          .to(this.$refs.text.$el, 1, {
            autoAlpha: 1,
            y: 0,
            ease: Power4.easeOut,
            clearProps: 'all',
          }, 'start+=0.15')
          .to(this.$refs.line, 1, {
            scaleY: 1,
            ease: Power4.easeOut,
          }, 'start+=0.3')
          .to(this.$refs.button.$el, 1, {
            autoAlpha: 1,
            y: 0,
            ease: Power4.easeOut,
            clearProps: 'all',
          }, 'start+=0.45');
      }

      tl.to(this.$refs.voices, 1, {
        autoAlpha: 1,
        y: 0,
        ease: Power4.easeOut,
        clearProps: 'all',
      }, this.short ? 'start' : 'start+=1');
    },
  },
  beforeDestroy() {
    if (!this.animated) raf.remove(this.id);
  },
};
</script>

<style lang="scss" scoped>
.footer {
  position: relative;
  height: 100vh;
  overflow: hidden;

  background-image: linear-gradient(134deg, #261E3C 0%, #9F7B8A 100%);

  &.footer--short {
    height: unset;
  }

  .section__line {
    height: 30px;
    margin: 25px 0;
  }

  &__sphere {
    width: 240px;
    height: 240px;
    margin-bottom: 35px;

    background-image: linear-gradient(40deg, #272345 15%, #E05D62 105%);
    border-radius: 50%;

    display: flex;
    align-items: center;
    justify-content: center;

    @include mq(ipadP) {
      width: 300px;
      height: 300px;
      margin-bottom: 50px;
    }
  }

  .footer__section {
    display: flex;
    justify-content: center;
    align-items: center;

    text-align: center;
    flex-flow: row wrap;
  }

  .footer__section--info {
    height: calc(100vh - 80px);
    flex-flow: column nowrap;
  }

  &.footer--short .footer__section--links {
    height: 100px;
  }

  .footer__section--links {
    height: 50px;

    a {
      font-weight: 300;
      font-size: 12px;
      padding: 0 20px;

      opacity: 0.75;
      transition: opacity 1s $outQuint 0.2s;

      cursor: pointer !important;
      pointer-events: all;
    }

    li {
      position: relative;

      &:first-child::before {
        content: none;
      }

      @include mq($until: ipadP) {
        flex: 0 0 30%;
        width: 30%;
        text-align: center;
        margin-bottom: 5px;

        a {
          padding: 0;
        }
      }

      @include mq(ipadP) {
        &::before {
          content: '';

          position: absolute;
          bottom: 2px;
          left: 0;
          width: 1px;
          height: calc(100% - 12px);
          background-color: white;
          opacity: 0.5;
        }

        @include mq($and: $is-not-touch-device) {
          & a {
            &::before {
              content: '';

              position: absolute;
              bottom: -10px;
              left: 20px;
              width: calc(100% - 40px);
              height: 1px;

              background-color: $color-white;
              transform-origin: 100% 50%;
              transform: scaleX(0);
              transition: transform 1s $outQuint;
            }

            &:hover {
              opacity: 1;

              &::before {
                transform-origin: 0 50%;
                transform: scaleX(1);
                background-color: rgba($color-white, 1);
              }
            }
          }
        }
      }
    }
  }
}
</style>
